// Single-source API strategy:
// always call backend routes as "/api/v1/..." and let Vite/reverse-proxy route them.
import {
    dispatchImpersonationExpired,
    isImpersonating,
} from './impersonation/session.js';

export const API_BASE_URL = '';


let inMemoryToken = null;

export const getAccessToken = () => inMemoryToken;
export const setAccessToken = (token) => {
    inMemoryToken = token;
    // Dispatch event so AuthContext knows the token updated (e.g., after refresh)
    window.dispatchEvent(new CustomEvent('token_changed', { detail: token }));
};
export const clearAccessToken = () => {
    inMemoryToken = null;
    window.dispatchEvent(new CustomEvent('token_changed', { detail: null }));
};

export const isTokenExpiringSoon = (token) => {
    if (!token) return false; // If no token, we can't proactively refresh
    try {
        const payloadBase64 = token.split('.')[1];
        if (!payloadBase64) return false;
        // Basic base64 decoding
        const decodedJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
        const decoded = JSON.parse(decodedJson);
        const exp = decoded.exp;
        if (!exp) return false;
        
        const nowInSeconds = Math.floor(Date.now() / 1000);
        // Refresh if it expires in less than 15 seconds
        return (exp - nowInSeconds) < 15;
    } catch (e) {
        return false;
    }
};

// Expose to window for debugging purposes in development
if (import.meta.env?.MODE === 'development') {
    window.getAccessToken = getAccessToken;
}

// Prevent multiple simultaneous refresh requests (Request Deduplication)
let refreshPromise = null;

// Guest pages where we shouldn't force log out or redirect
const GUEST_ROUTES = ['/login', '/register', '/forgot-password', '/verify'];
const isOnGuestPage = () => GUEST_ROUTES.some((r) => window.location.pathname.startsWith(r));

/**
 * Performs the actual refresh call to the backend.
 * Uses credentials: 'include' to send the HttpOnly refresh cookie.
 */
export const doRefresh = async () => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    })
        .then(async (res) => {
            if (!res.ok) throw new Error(`REFRESH_FAILED_${res.status}`);
            const data = await res.json();
            if (!data.access_token) throw new Error('NO_TOKEN_IN_RESPONSE');

            setAccessToken(data.access_token);
            localStorage.setItem('has_session', 'true');
            return data.access_token;
        })
        .catch((err) => {
            // Aggressive Logout: If the refresh fails for ANY reason (400, 401, 500, missing cookie), 
            // we must assume the session is dead to prevent an endless error loop.
            clearAccessToken();
            localStorage.removeItem('user');
            localStorage.removeItem('has_session');
            
                        if (!isOnGuestPage()) {
                const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
                const isViedma = currentPath.includes('/viedma');
                const isQuran = currentPath.startsWith('/quran') || currentPath.includes('/quran');

                if (isViedma) {
                    window.location.href = '/login?portal=viedma&redirect=/app/viedma&expired=true';
                } else if (isQuran) {
                    window.location.href = '/login?portal=quran&redirect=/quran&expired=true';
                } else {
                    const redirectParam = currentPath && currentPath !== '/' ? '&redirect=' + encodeURIComponent(currentPath) : '';
                    window.location.href = '/login?expired=true' + redirectParam;
                }
            }
            throw err;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};

const DEFAULT_TIMEOUT = 60000; // 60 seconds

const getErrorCodesMap = async () => {
    return {};
};

// ─── Core fetch with Interceptor ─────────────────────────────────────────────
export const apiFetch = async (endpoint, options = {}, _retry = true) => {
    const url = endpoint;
    const token = getAccessToken();

    const { headers, timeout = DEFAULT_TIMEOUT, signal: externalSignal, ...restOptions } = options;
    const isFormData = restOptions.body instanceof FormData;

    // Use AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // If an external signal is provided, we should listen to it to abort our local controller.
    if (externalSignal) {
        if (externalSignal.aborted) {
            clearTimeout(timeoutId);
            throw externalSignal.reason || new Error('Aborted');
        }
        externalSignal.addEventListener('abort', () => {
            controller.abort();
            clearTimeout(timeoutId);
        }, { once: true });
    }

    const builtOptions = {
        credentials: 'include',
        ...restOptions,
        signal: controller.signal,
        headers: {
            'Accept': 'application/json',
            ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...headers,
        },
    };

    try {
        // ── Proactive Refresh Check ──────────────────────────────────────────────
        // Before we even make the request, check if the token is about to expire
        if (token && _retry && !isOnGuestPage() && isTokenExpiringSoon(token)) {
            try {
                await doRefresh();
                // Update the token for this request
                builtOptions.headers.Authorization = `Bearer ${getAccessToken()}`;
            } catch (refreshErr) {
                // If proactive refresh fails, we let the request proceed (it will likely fail 401)
                // or just throw immediately. We'll throw immediately to avoid the 401 error.
                throw Object.assign(new Error('SESSION_EXPIRED'), { status: 401 });
            }
        }

        const response = await fetch(url, builtOptions);

        // ── Fallback Auto-refresh Interceptor (if 401 still happens) ─────────────
        if (response.status === 401 && _retry && !isOnGuestPage()) {
            if (isImpersonating()) {
                dispatchImpersonationExpired();
                throw Object.assign(new Error('IMPERSONATION_EXPIRED'), { status: 401 });
            }
            try {
                await doRefresh();
                // Successfully refreshed! Retry the original request with the new token.
                return apiFetch(endpoint, options, false);
            } catch (refreshErr) {
                // Authentication is definitively lost.
                throw Object.assign(new Error('SESSION_EXPIRED'), { status: 401 });
            }
        }

        // ── Global Error Handling ──────────────────────────────────────────────────
        if (!response.ok) {
            let errorData = {};
            let rawText = '';
            try {
                rawText = await response.text();
                errorData = JSON.parse(rawText);
            } catch (e) {
                console.error("Failed to parse error JSON. Raw text:", rawText);
                errorData = { detail: rawText || `HTTP ${response.status}` };
            }
            
            let errorMessage = 'API Error';
            if (Array.isArray(errorData.detail)) {
                errorMessage = errorData.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
            } else if (errorData.detail) {
                errorMessage = errorData.detail;
            } else if (errorData.message) {
                errorMessage = errorData.message;
            } else if (errorData.error) {
                errorMessage = errorData.error;
            } else if (errorData.errors) {
                errorMessage = errorData.errors.general || Object.values(errorData.errors)[0] || 'API Error';
            }

            const codesMap = await getErrorCodesMap();
            if (codesMap && typeof errorMessage === 'string') {
                // Try to match the exact string, or a specific code field
                if (codesMap[errorMessage]) {
                    errorMessage = codesMap[errorMessage];
                } else if (errorData.code && codesMap[errorData.code]) {
                    errorMessage = codesMap[errorData.code];
                }
            }

            throw Object.assign(new Error(errorMessage), {
                status: response.status,
                data: errorData
            });
        }

        if (response.status === 204) return null;
        return response.json();

    } catch (err) {
        if (err.name !== 'AbortError' && (!err.status || err.status >= 500)) {
            console.error(`API Fetch Error [${builtOptions.method || 'GET'}] ${url}:`, err);
        }
        if (err.name === 'AbortError') {
            // If it wasn't aborted by the user (externalSignal), then it was a timeout.
            if (!externalSignal?.aborted) {
                throw Object.assign(new Error('REQUEST_TIMEOUT'), { status: 408 });
            }
        }
        throw err;
    } finally {
        clearTimeout(timeoutId);
    }
};


export const logoutApi = async () => {
    try {
        await fetch('/api/v1/auth/logout', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {})
            }
        });
    } catch (err) {
        // Ignore network errors on logout
    } finally {
        clearAccessToken();
        localStorage.removeItem('has_session');
    }
};

export const fetcher = (url) => apiFetch(url);

const buildAvatarUrl = (user) => {
    let baseUrl;

    if (user?.avatar_display_url) {
        baseUrl = user.avatar_display_url;
    } else if (!user?.avatar_url) {
        return '/avatar-default.png';
    } else {
        baseUrl = user.avatar_url.startsWith('http')
            ? user.avatar_url
            : (user.avatar_url.startsWith('/') ? user.avatar_url : `/${user.avatar_url}`);
    }

    // Add a cache buster if we have an updated_at timestamp from the user object
    if (user?.updated_at) {
        const parsed = new Date(user.updated_at).getTime();
        const timestamp = isNaN(parsed) ? user.updated_at : parsed;
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}t=${timestamp}`;
    }
    
    return baseUrl;
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

export const stripTimestampParam = (url) => {
    if (!url || typeof url !== 'string') return '';
    return url.replace(/([?&])t=[^&]+(&|$)/, '$1').replace(/[?&]$/, '');
};

export const isSameAvatarSource = (cachedSource, currentSource) => {
    if (!cachedSource || !currentSource) return cachedSource === currentSource;
    if (cachedSource === currentSource) return true;
    const cleanCached = stripTimestampParam(cachedSource).trim();
    const cleanCurrent = stripTimestampParam(currentSource).trim();
    if (cleanCached === cleanCurrent) return true;
    if (cleanCached.endsWith(cleanCurrent) || cleanCurrent.endsWith(cleanCached)) return true;
    return false;
};

export const cacheAvatarForUser = async (user) => {
    if (!user) return user;

    const avatarUrl = buildAvatarUrl(user);
    if (!avatarUrl || avatarUrl === '/avatar-default.png') {
        return {
            ...user,
            avatar_cached_data_url: null,
            avatar_cached_source_url: null,
        };
    }

    if (
        user.avatar_cached_data_url &&
        user.avatar_cached_source_url &&
        isSameAvatarSource(user.avatar_cached_source_url, avatarUrl)
    ) {
        return user;
    }

    try {
        const response = await fetch(avatarUrl, {
            credentials: 'include',
            cache: 'no-cache',
        });

        if (!response.ok) {
            throw new Error(`AVATAR_FETCH_FAILED_${response.status}`);
        }

        const avatarBlob = await response.blob();
        const avatarDataUrl = await blobToDataUrl(avatarBlob);

        return {
            ...user,
            avatar_cached_data_url: avatarDataUrl,
            avatar_cached_source_url: avatarUrl,
        };
    } catch {
        return {
            ...user,
            avatar_cached_data_url: user.avatar_cached_data_url || null,
            avatar_cached_source_url: avatarUrl,
        };
    }
};

export const hasCustomAvatar = (user) => {
    const url = getAvatarUrl(user);
    return url && url !== '/avatar-default.png';
};

export const getAvatarUrl = (user) => {
    const avatarUrl = buildAvatarUrl(user);

    if (
        avatarUrl &&
        avatarUrl !== '/avatar-default.png' &&
        user?.avatar_cached_data_url &&
        isSameAvatarSource(user?.avatar_cached_source_url, avatarUrl)
    ) {
        return user.avatar_cached_data_url;
    }

    return avatarUrl;
};
